import { LogOut } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.tsx';

/**
 * Menú de la persona conectada, a la derecha del top-bar.
 *
 * En el sidebar el correo y el botón de salir vivían siempre visibles al pie.
 * Acá van detrás de un disparador porque la barra horizontal es angosta y el
 * correo largo empujaría la navegación.
 */
export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  // Cerrar al hacer clic afuera y con Escape. Un menú que sólo cierra con su
  // propio botón queda tapando la pantalla cuando la persona sigue navegando.
  useEffect(() => {
    if (!abierto) return;

    const alClic = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };

    document.addEventListener('mousedown', alClic);
    document.addEventListener('keydown', alTeclado);
    return () => {
      document.removeEventListener('mousedown', alClic);
      document.removeEventListener('keydown', alTeclado);
    };
  }, [abierto]);

  function cerrarSesion() {
    logout();
    navigate('/login');
  }

  const correo = user?.email ?? '—';
  // Con el correo entero la barra se desarma en pantallas chicas; la inicial
  // basta para reconocerse, y el correo completo está adentro del menú.
  const inicial = correo.charAt(0).toUpperCase();

  return (
    <div ref={contenedor} className="relative">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={`Cuenta de ${correo}`}
        data-testid="user-menu-trigger"
        className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-brand-soft text-sm font-medium text-content-brand transition-colors hover:bg-surface-sunken"
      >
        {inicial}
      </button>

      {abierto ? (
        <div
          role="menu"
          data-testid="user-menu"
          className="absolute right-0 z-50 mt-2 w-64 rounded-lg border border-edge-subtle bg-surface-raised py-1 shadow-lg"
        >
          <div className="truncate border-b border-edge-subtle px-4 py-2 text-sm text-content-secondary">
            {correo}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={cerrarSesion}
            className="flex w-full items-center gap-3 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-sunken"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
